import React from 'react';
import toast from 'react-hot-toast';
import CorrectionModalBase from './CorrectionModalBase';
import api from '../services/api';

// Predefiniowane powody korekty
const CORRECTION_REASONS = [
  { value: '', label: '-- Wybierz powód korekty --' },
  { value: 'blad_ceny', label: 'Błąd w cenie' },
  { value: 'blad_ilosci', label: 'Błąd w ilości' },
  { value: 'zwrot_towaru', label: 'Zwrot towaru' },
  { value: 'reklamacja', label: 'Reklamacja' },
  { value: 'rabat_potransakcyjny', label: 'Rabat potransakcyjny' },
  { value: 'pomylka_w_danych', label: 'Pomyłka w danych nabywcy' },
  { value: 'niezgodnosc_dostawy', label: 'Niezgodność z dostawą' },
  { value: 'inne', label: 'Inny powód' }
];

// --- Configuration callbacks ---

const invoiceInfoRows = (invoice) => [
  { label: 'Data wystawienia', value: invoice?.data_wystawienia || '-' },
  { label: 'Wartość faktury', value: `${parseFloat(invoice?.suma_brutto || 0).toFixed(2)} zł` },
  { label: 'Nabywca', value: invoice?.nabywca_nazwa || '-' }
];

const itemSubtitle = (item) => (
  <>
    {item.kod_produktu && <span className="me-1">Kod: {item.kod_produktu}</span>}
    <span>VAT: {item.stawka_vat}%</span>
  </>
);

const calculateRowDifference = (item, sel) => {
  const originalValue = parseFloat(item.ilosc) * parseFloat(item.cena_jednostkowa_brutto);
  const newValue = (sel.quantity || 0) * (sel.newPrice || 0);
  return newValue - originalValue;
};

const calculateTotal = (items, selectedItems) => {
  let total = 0;
  items.forEach(item => {
    const sel = selectedItems[item.id];
    if (sel?.selected) {
      const originalValue = parseFloat(item.ilosc) * parseFloat(item.cena_jednostkowa_brutto);
      const newValue = sel.quantity * sel.newPrice;
      total += (newValue - originalValue);
    }
  });
  return total;
};

const buildCorrectionItem = (item, sel, { iloscKorekty, originalQty, newQty, getFullReason }) => {
  const originalPrice = parseFloat(item.cena_jednostkowa_netto) || (parseFloat(item.cena_jednostkowa_brutto) / (1 + parseFloat(item.stawka_vat) / 100));
  const stawkaVat = parseFloat(item.stawka_vat) || 23;
  const newPriceNetto = sel.newPrice / (1 + stawkaVat / 100);

  return {
    product_id: item.product_id,
    pozycja_oryginalna_id: item.id,
    nazwa_produktu: item.nazwa_produktu,
    kod_produktu: item.kod_produktu,
    jednostka: item.jednostka || 'szt',
    ilosc: iloscKorekty,
    ilosc_oryginalna: originalQty,
    ilosc_po_korekcie: newQty,
    cena_jednostkowa_netto: newPriceNetto,
    cena_oryginalna_netto: originalPrice,
    stawka_vat: stawkaVat,
    powod: sel.reason || getFullReason()
  };
};

const buildSubmitBody = ({ correctionItems, getFullReason, paymentMethod, authUser }) => ({
  pozycje: correctionItems,
  przyczyna_korekty: getFullReason(),
  metoda_platnosci: paymentMethod,
  created_by: authUser?.login || 'admin'
});

const onSuccessData = (apiData, { correctionItems, getFullReason, paymentMethod, invoice }) => ({
  correctionData: {
    ...apiData,
    items: correctionItems,
    reason: getFullReason(),
    payment_method: paymentMethod,
    invoice: invoice
  },
  successMessage: `Korekta ${apiData.numer_faktury} została utworzona pomyślnie!`
});

// Print handler - PDF only (endpoint handles both regular and correction invoices)
const handlePrint = ({ correctionData }) => {
  const openPdf = async () => {
    try {
      const response = await api.get(`/sales-invoices/${correctionData.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Nie udalo sie wygenerowac PDF korekty.');
    }
  };
  openPdf();
};

// Payment section renderer (inline radio buttons + reason dropdown)
const renderPaymentSection = ({ paymentMethod, setPaymentMethod, reason, setReason, customReason, setCustomReason, correctionReasons }) => (
  <>
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Metoda rozliczenia korekty:
      </label>
      <div className="btn-group w-100" role="group">
        <input type="radio" className="btn-check" name="paymentMethod" id="pm_gotowka"
          checked={paymentMethod === 'gotowka'} onChange={() => setPaymentMethod('gotowka')} />
        <label className="btn btn-outline-success btn-sm py-1" htmlFor="pm_gotowka" style={{ fontSize: '10px' }}>
          <i className="fas fa-money-bill-wave me-1"></i>Gotówka
        </label>

        <input type="radio" className="btn-check" name="paymentMethod" id="pm_karta"
          checked={paymentMethod === 'karta'} onChange={() => setPaymentMethod('karta')} />
        <label className="btn btn-outline-primary btn-sm py-1" htmlFor="pm_karta" style={{ fontSize: '10px' }}>
          <i className="fas fa-credit-card me-1"></i>Karta
        </label>

        <input type="radio" className="btn-check" name="paymentMethod" id="pm_przelew"
          checked={paymentMethod === 'przelew'} onChange={() => setPaymentMethod('przelew')} />
        <label className="btn btn-outline-info btn-sm py-1" htmlFor="pm_przelew" style={{ fontSize: '10px' }}>
          <i className="fas fa-university me-1"></i>Przelew
        </label>
      </div>
    </div>
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Powód korekty: <span className="text-danger">*</span>
      </label>
      <select
        className="form-select form-select-sm"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{ fontSize: '11px' }}
      >
        {correctionReasons.map(r => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      {reason === 'inne' && (
        <input
          type="text"
          className="form-control form-control-sm mt-1"
          placeholder="Opisz powód korekty..."
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          style={{ fontSize: '11px' }}
        />
      )}
    </div>
  </>
);

// --- Component ---

const InvoiceCorrectionModal = (props) => (
  <CorrectionModalBase
    {...props}
    correctionReasons={CORRECTION_REASONS}
    title={(invoice) => `Korekta faktury ${invoice?.numer_faktury || `#${invoice?.id}`}`}
    apiBase="sales-invoices"
    itemsKey="pozycje"
    priceField="cena_jednostkowa_brutto"
    priceColumnLabels={{ original: 'Cena oryg.', new: 'Nowa cena' }}
    invoiceInfoRows={invoiceInfoRows}
    itemSubtitle={itemSubtitle}
    calculateRowDifference={calculateRowDifference}
    calculateTotal={calculateTotal}
    differenceColorClasses={{ negative: 'text-danger', positive: 'text-success' }}
    summaryColors={{ refund: 'var(--danger-light)', surcharge: 'var(--success-light)' }}
    summaryLabels={{ refund: 'Kwota do zwrotu klientowi:', surcharge: 'Kwota do dopłaty przez klienta:' }}
    summaryTextClasses={{ refund: 'text-danger', surcharge: 'text-success' }}
    buildCorrectionItem={buildCorrectionItem}
    buildSubmitBody={buildSubmitBody}
    onSuccessData={onSuccessData}
    renderPaymentSection={renderPaymentSection}
    handlePrint={handlePrint}
    defaultPaymentMethod="gotowka"
  />
);

export default InvoiceCorrectionModal;
